import { Component } from 'preact';
import { motion, AnimatePresence } from 'framer-motion';
import { RADIUS, TEXT_MUTED, TEXT_PRIMARY, OPAQUE_PANEL_BG, GOLD_TEXT } from '../../lib/medievalTheme';
import { getClass } from '../../utils/arena/classes';
import { ALCHEMIST_POTIONS, ALCHEMIST_YIELD_NODES, HUMOURS, HUMOUR_SCALE, POTION_RECIPES, potionArt, potionArtMask, potionPotency, potionEffect } from '../../utils/arena/alchemist';
import ElementIcon from '../deckStats/ElementIcon';
import { playUI, UI } from '../../utils/arena/uiSounds';
import { t } from '../../localization/t';

const ACCENT = getClass('alchemist').accent;
const SLAIN = { Water: 'slainWater', Air: 'slainAir', Earth: 'slainEarth', Fire: 'slainFire' };

// Humours gathered so far this match, straight from the mirror's kill
// counters, per element. Read for a seat.
export function harvest(stats, seat) {
  const mine = stats?.[seat] || {};
  return Object.fromEntries(HUMOURS.map((h) => [h.key, (mine[SLAIN[h.element]] || 0) * HUMOUR_SCALE]));
}

// Fixation adds this much of the perk-adjusted harvest per unit of its
// potency's power. Mirrors FIXATION_BONUS on the server.
const FIXATION_BONUS = 0.2;

// What the settle will bank per humour: the raw harvest, the yield perks
// held, then Fixation on top. The same arithmetic as the server's
// settleAlchemist, so the chip shows what the match ends with.
export function projectHarvest(stats, seat, skills, alchemist) {
  const raw = harvest(stats, seat);
  const fixation = alchemist?.active?.fixation?.uses > 0 ? potionPotency(alchemist.active.fixation.potency || 'ordinary')?.power || 1 : 0;
  const held = new Set(skills?.pointsSpent || []);
  const out = {};
  for (const h of HUMOURS) {
    const base = raw[h.key];
    const perks = ALCHEMIST_YIELD_NODES.filter((n) => n.humour === h.key && held.has(n.key)).reduce((a, n) => a + n.yieldBonus * 100, 0);
    const normal = base ? Math.floor(base * (100 + perks) / 100) : 0;
    const bonus = fixation && normal ? Math.floor(normal * (FIXATION_BONUS * 100) * fixation / 100) : 0;
    out[h.key] = { base, normal, bonus, total: normal + bonus };
  }
  return out;
}

// One count on the chip: a new value rolls in from below as the old rolls
// out above, so a rise is seen, not just read.
function RollingCount({ value, lit }) {
  return (
    <span className="relative inline-flex overflow-hidden tabular-nums" style={{ height: '1.35cqw', minWidth: `${Math.max(1.1, String(value).length * 0.75)}cqw`, justifyContent: 'center' }}>
      <AnimatePresence initial={false}>
        <motion.span
          key={value}
          className="arena-heading absolute"
          style={{ color: lit ? TEXT_PRIMARY : TEXT_MUTED, fontSize: '1.125cqw', lineHeight: '1.35cqw' }}
          initial={{ y: '1.35cqw', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '-1.35cqw', opacity: 0 }}
          transition={{ type: 'spring', duration: 0.45, bounce: 0 }}
        >
          {value}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

// The Alchemist's chip at the top of the board: the four humours as they
// are harvested. A kill during combat waits until the combat overlay has
// gone (`hold`), then the count rolls up with a soft chime. Hovering lays
// out the potions readied for this match.
export default class HumourChip extends Component {
  constructor(props) {
    super(props);
    this.state = { open: false, flash: null, shown: this.projected(props) };
  }

  projected(props = this.props) {
    const projected = projectHarvest(props.stats, props.seat, props.skills, props.alchemist);
    return Object.fromEntries(HUMOURS.map((h) => [h.key, projected[h.key].total]));
  }

  // The overlay fades for 250ms after the stage clears; the roll waits it out.
  static RELEASE_MS = 400;

  componentDidUpdate() {
    if (this.props.hold) { clearTimeout(this._releaseTimer); this._releaseTimer = null; return; }
    const now = this.projected();
    if (!HUMOURS.some((h) => now[h.key] !== this.state.shown[h.key]) || this._releaseTimer) return;
    this._releaseTimer = setTimeout(() => {
      this._releaseTimer = null;
      if (this.props.hold) return;
      const next = this.projected();
      const grew = HUMOURS.filter((h) => next[h.key] !== this.state.shown[h.key]);
      if (!grew.length) return;
      if (grew.some((h) => next[h.key] > this.state.shown[h.key])) playUI(UI.GOLD, { volume: 0.3 });
      this.setState({ shown: next, flash: grew.map((h) => h.key) });
      clearTimeout(this._flashTimer);
      this._flashTimer = setTimeout(() => this.setState({ flash: null }), 900);
    }, HumourChip.RELEASE_MS);
  }

  componentWillUnmount() { clearTimeout(this._flashTimer); clearTimeout(this._releaseTimer); }

  render() {
    const { alchemist } = this.props;
    const counts = this.state.shown;
    const readied = ALCHEMIST_POTIONS.filter((p) => POTION_RECIPES[p.key].kind === 'match' && alchemist?.active?.[p.key]?.uses > 0);
    return (
      <div
        className="fixed top-3 left-1/2 -translate-x-1/2 z-[999]"
        onMouseEnter={() => this.setState({ open: true })}
        onMouseLeave={() => this.setState({ open: false })}
        data-testid="humour-chip"
      >
        <div
          className="flex items-center"
          style={{
            gap: '1.25cqw', padding: '0.5cqw 1.25cqw',
            borderRadius: RADIUS.lg, backgroundColor: OPAQUE_PANEL_BG,
            boxShadow: `0 0 0 1px ${GOLD_TEXT} 0.45), 0 0.25cqw 0.75cqw rgba(0,0,0,0.6)`,
          }}
        >
          {HUMOURS.map((h) => (
            <motion.span
              key={h.key} className="flex items-center" style={{ gap: '0.45cqw' }}
              animate={this.state.flash?.includes(h.key) ? { scale: [1, 1.2, 1], filter: [`drop-shadow(0 0 0 ${ACCENT}00)`, `drop-shadow(0 0 0.5cqw ${ACCENT})`, `drop-shadow(0 0 0 ${ACCENT}00)`] } : { scale: 1 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              data-testid={`harvest-${h.key}`}
            >
              <ElementIcon element={h.element} size={24} muted={counts[h.key] === 0} />
              <RollingCount value={counts[h.key]} lit={counts[h.key] > 0} />
            </motion.span>
          ))}
          {readied.length ? (
            <span className="flex items-center" style={{ gap: '0.375cqw', paddingLeft: '0.75cqw', borderLeft: `1px solid ${GOLD_TEXT} 0.25)` }}>
              {readied.map((p) => (
                <img key={p.key} src={potionArt(p.key)} alt="" draggable={false} style={{ width: '2.25cqw', height: '2.25cqw', objectFit: 'contain', maskImage: potionArtMask(p.key), maskSize: '100% 100%' }} />
              ))}
            </span>
          ) : null}
        </div>
        {/* Hover: the match potions at work, nothing else. */}
        <AnimatePresence>
          {this.state.open && readied.length ? (
            <motion.div
              className="absolute left-1/2 flex flex-col"
              style={{ top: '100%', marginTop: '0.625cqw', x: '-50%', width: '30cqw', padding: '1cqw 1.25cqw', gap: '0.875cqw', borderRadius: RADIUS['2xl'], backgroundColor: OPAQUE_PANEL_BG, boxShadow: `0 0 0 1px ${GOLD_TEXT} 0.35), 0 0.5cqw 1cqw rgba(0,0,0,0.6)` }}
              initial={{ opacity: 0, y: '-0.5cqw', x: '-50%' }}
              animate={{ opacity: 1, y: 0, x: '-50%' }}
              exit={{ opacity: 0, y: '-0.375cqw', x: '-50%' }}
              transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
              data-testid="humour-chip-detail"
            >
              {readied.map((p) => {
                const potency = alchemist.active[p.key].potency || 'ordinary';
                return (
                  <div key={p.key} className="flex items-center" style={{ gap: '0.875cqw' }}>
                    <img src={potionArt(p.key)} alt="" draggable={false} style={{ width: '3.5cqw', height: '3.5cqw', objectFit: 'contain', maskImage: potionArtMask(p.key), maskSize: '100% 100%' }} />
                    <div className="flex flex-col" style={{ gap: '0.2cqw' }}>
                      <span className="arena-heading" style={{ color: ACCENT, fontSize: '1cqw' }}>{t('{potency} Potion of {name}', { potency: t(potionPotency(potency)?.title || 'Ordinary'), name: t(p.title) })}</span>
                      <span style={{ color: TEXT_PRIMARY, fontSize: '0.875cqw', lineHeight: 1.4 }}>{potionEffect(p.key, potency)}</span>
                    </div>
                  </div>
                );
              })}
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    );
  }
}
